import { ReactNode, PropsWithoutRef } from "react"
import { Form as FinalForm, FormProps as FinalFormProps, FormSpy } from "react-final-form"
import { z } from "zod"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Alert from "@mui/material/Alert"
export { FORM_ERROR } from "final-form"
import { omit } from "lodash"
import { validateZodSchema } from "blitz"

export interface FormProps<S extends z.ZodType<any, any>>
  extends Omit<PropsWithoutRef<JSX.IntrinsicElements["form"]>, "onSubmit"> {
  /** All your form fields */
  children?: ReactNode
  /** Text to display in the submit button */
  submitText?: string
  cancelText?: string
  schema?: S
  mutators?: FinalFormProps<z.infer<S>>["mutators"]
  onSubmit: FinalFormProps<z.infer<S>>["onSubmit"]
  onCancel?: () => void
  initialValues?: FinalFormProps<z.infer<S>>["initialValues"]
}

export function Form<S extends z.ZodType<any, any>>({
  children,
  submitText,
  cancelText,
  schema,
  mutators,
  initialValues,
  onSubmit,
  onCancel,
  ...props
}: FormProps<S>) {
  return (
    <FinalForm
      initialValues={initialValues}
      validate={validateZodSchema(schema)}
      onSubmit={onSubmit}
      mutators={mutators}
      keepDirtyOnReinitialize
      render={({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit} className="form" {...omit(props, ["setSnackbarData"])}>
          {/* Form fields supplied as children are rendered here */}
          {children}

          {submitError && (
            <Alert severity="error" sx={{ my: 2 }}>
              {submitError}
            </Alert>
          )}

          {submitText && (
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" type="submit" disabled={submitting}>
                {submitText}
              </Button>
              {cancelText && (
                <Button variant="text" onClick={onCancel}>
                  {cancelText}
                </Button>
              )}
            </Box>
          )}
          {process.env.NODE_ENV === "development" && (
            <FormSpy subscription={{ values: true, errors: true }}>
              {(form) => <pre>{JSON.stringify(form, undefined, 2)}</pre>}
            </FormSpy>
          )}
        </form>
      )}
    />
  )
}

export default Form
